import { useTranslation } from 'react-i18next'
import { Card, Badge } from '@agion/ui'
import { MapPin } from 'lucide-react'
import { CloudProviderIcon } from './InfraCloudProviderIcon'
import { DeploymentModelBadge } from './InfraDeploymentModelBadge'
import type { CloudEnvironment, EnvironmentStatus } from '@/core/infra_types'
import { formatRelativeTime } from '@/core/infra_utils'

interface EnvironmentCardProps {
  environment: CloudEnvironment
  onClick?: (env: CloudEnvironment) => void
}

const statusVariantMap: Record<EnvironmentStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'default',
  provisioning: 'info',
  active: 'success',
  updating: 'info',
  failed: 'error',
  decommissioning: 'warning',
  decommissioned: 'default',
}

export function EnvironmentCard({ environment, onClick }: EnvironmentCardProps) {
  const { t } = useTranslation(['pages'])

  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md"
      onClick={() => onClick?.(environment)}
    >
      <div className="p-5">
        {/* Header: Provider icon + Name + Status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <CloudProviderIcon provider={environment.cloudProvider} size={24} />
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {environment.name}
              </h3>
              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                <MapPin size={12} />
                <span>{environment.region}</span>
              </div>
            </div>
          </div>
          <Badge variant={statusVariantMap[environment.status] ?? 'default'}>
            {t(`pages:environments.status.${environment.status}`, environment.status)}
          </Badge>
        </div>

        {/* Deployment model */}
        <div className="flex items-center justify-between mt-4">
          <DeploymentModelBadge model={environment.deploymentModel} />
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {formatRelativeTime(environment.updatedAt)}
          </span>
        </div>
      </div>
    </Card>
  )
}
